import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  useTheme,
  SelectChangeEvent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  FormatPaint as TemplateIcon,
  SaveAlt as SaveIcon,
  Cancel as CancelIcon,
  CalendarViewMonth as TableIcon,
  TextFormat as TextIcon,
  Image as ImageIcon,
  QrCode as BarcodeIcon,
  FileUpload as ImportIcon,
  FileDownload as ExportIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as printTemplateService from '../../services/printTemplateService';
import { InvoiceType } from '../../types/invoice';
import { 
  IPrintTemplate, 
  IPrintTemplateCreate,
  PaperSize,
  Orientation,
  Language
} from '../../types/printTemplate';

// تعريف أنواع الفاتورة
const invoiceTypes = [
  { value: 'sales_invoice', label: 'فاتورة مبيعات' },
  { value: 'sales_return', label: 'مرتجع مبيعات' },
  { value: 'purchase_invoice', label: 'فاتورة مشتريات' },
  { value: 'purchase_return', label: 'مرتجع مشتريات' },
  { value: 'quotation', label: 'عرض سعر' },
  { value: 'beginning_inventory', label: 'بضاعة أول المدة' }
];

// تعريف أحجام الورق
const paperSizes = [
  { value: 'A4', label: 'A4 (210×297mm)' },
  { value: 'A5', label: 'A5 (148×210mm)' },
  { value: 'A6', label: 'A6 (105×148mm)' },
  { value: 'thermal80mm', label: 'حراري 80mm' },
  { value: 'thermal58mm', label: 'حراري 58mm' },
  { value: 'custom', label: 'حجم مخصص' }
];

// تعريف اتجاهات الورق
const orientations = [
  { value: 'portrait', label: 'رأسي' },
  { value: 'landscape', label: 'أفقي' }
];

// تعريف اللغات
const languages = [
  { value: 'ar', label: 'العربية' },
  { value: 'en', label: 'الإنجليزية' }
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`template-tabpanel-${index}`}
      aria-labelledby={`template-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `template-tab-${index}`,
    'aria-controls': `template-tabpanel-${index}`,
  };
}

const PrintTemplateSettingsPage: React.FC = () => {
  const [templates, setTemplates] = useState<IPrintTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<IPrintTemplate | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [filterType, setFilterType] = useState<string>('');
  const [filterLanguage, setFilterLanguage] = useState<string>('');
  
  // حالات الاستيراد والتصدير
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [importJsonError, setImportJsonError] = useState<string | null>(null);
  const [parsedTemplates, setParsedTemplates] = useState<IPrintTemplate[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<IPrintTemplateCreate>({
    name: '',
    description: '',
    invoiceType: 'sales_invoice',
    paperSize: 'A4',
    orientation: 'portrait',
    displayLanguage: 'ar',
    isDefault: false,
    elements: []
  });

  const navigate = useNavigate();
  const theme = useTheme();

  // جلب قوالب الطباعة
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const data = await printTemplateService.getPrintTemplates(filterType, filterLanguage);
        setTemplates(data);
        setError(null);
      } catch (err) {
        console.error('خطأ في جلب قوالب الطباعة:', err);
        setError('حدث خطأ أثناء تحميل قوالب الطباعة. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [filterType, filterLanguage]);

  // تغيير علامة التبويب
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // تغيير عامل التصفية
  const handleFilterChange = (type: string, e: SelectChangeEvent) => {
    if (type === 'invoiceType') {
      setFilterType(e.target.value);
    } else if (type === 'displayLanguage') {
      setFilterLanguage(e.target.value);
    }
  };

  // تغييرات في نموذج الإدخال
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    
    if (e.target instanceof HTMLInputElement && e.target.type === 'checkbox') {
      const checked = e.target.checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // فتح مربع حوار إنشاء قالب جديد
  const handleOpenCreateDialog = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      invoiceType: 'sales_invoice',
      paperSize: 'A4',
      orientation: 'portrait',
      displayLanguage: 'ar',
      isDefault: false,
      elements: []
    });
    setOpenDialog(true);
  };

  // فتح مربع حوار تعديل قالب
  const handleOpenEditDialog = (template: IPrintTemplate) => {
    setEditingTemplate(template);
    
    const formDataObj: IPrintTemplateCreate = {
      name: template.name,
      description: template.description || '',
      invoiceType: template.invoiceType,
      paperSize: template.paperSize,
      orientation: template.orientation,
      customWidth: template.customWidth,
      customHeight: template.customHeight,
      displayLanguage: template.displayLanguage, // Updated to match renamed field
      isDefault: template.isDefault,
      elements: template.elements || []
    };
    
    setFormData(formDataObj);
    setOpenDialog(true);
  };

  // إغلاق مربع الحوار
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // إرسال النموذج لإنشاء أو تحديث قالب الطباعة
  const handleSubmit = async () => {
    try {
      if (!formData.name) {
        setError('يرجى إدخال اسم للقالب');
        return;
      }

      // التحقق من أبعاد الورق المخصص إذا تم اختيار حجم مخصص
      if (formData.paperSize === 'custom' && (!formData.customWidth || !formData.customHeight)) {
        setError('يرجى تحديد أبعاد الورق المخصص');
        return;
      }

      let updatedTemplate;
      
      if (editingTemplate) {
        // تحديث قالب موجود
        updatedTemplate = await printTemplateService.updatePrintTemplate(
          editingTemplate._id!,
          formData
        );
        
        // تحديث قائمة القوالب
        setTemplates(templates.map(template =>
          template._id === editingTemplate._id ? updatedTemplate : template
        ));
      } else {
        // إنشاء قالب جديد
        updatedTemplate = await printTemplateService.createPrintTemplate(formData);
        setTemplates([...templates, updatedTemplate]);
      }

      setOpenDialog(false);
      setError(null);
    } catch (err) {
      console.error('خطأ في حفظ القالب:', err);
      setError('حدث خطأ أثناء حفظ القالب. يرجى المحاولة مرة أخرى.');
    }
  };

  // حذف قالب طباعة
  const handleDeleteTemplate = async (templateId: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا القالب؟')) {
      try {
        await printTemplateService.deletePrintTemplate(templateId);
        setTemplates(templates.filter(template => template._id !== templateId));
      } catch (err) {
        console.error('خطأ في حذف القالب:', err);
        setError('حدث خطأ أثناء حذف القالب. يرجى المحاولة مرة أخرى.');
      }
    }
  };

  // تعيين قالب كافتراضي
  const handleSetDefaultTemplate = async (templateId: string) => {
    try {
      const updatedTemplate = await printTemplateService.setDefaultTemplate(templateId);
      
      // تحديث حالة "افتراضي" لجميع القوالب
      setTemplates(templates.map(template => ({
        ...template,
        isDefault: template._id === templateId
      })));
    } catch (err) {
      console.error('خطأ في تعيين القالب كافتراضي:', err);
      setError('حدث خطأ أثناء تعيين القالب كافتراضي. يرجى المحاولة مرة أخرى.');
    }
  };

  // استنساخ قالب
  const handleCloneTemplate = async (templateId: string) => {
    const originalTemplate = templates.find(t => t._id === templateId);
    if (!originalTemplate) return;
    
    const newName = `نسخة من ${originalTemplate.name}`;
    
    try {
      const clonedTemplate = await printTemplateService.clonePrintTemplate(templateId, newName);
      setTemplates([...templates, clonedTemplate]);
    } catch (err) {
      console.error('خطأ في استنساخ القالب:', err);
      setError('حدث خطأ أثناء استنساخ القالب. يرجى المحاولة مرة أخرى.');
    }
  };

  // تصدير القوالب المحددة
  const handleExportTemplates = async () => {
    try {
      setLoading(true);
      const result = await printTemplateService.exportPrintTemplates(
        selectedTemplates.length > 0 ? selectedTemplates : undefined
      );
      
      // تنزيل الملف
      const filename = selectedTemplates.length > 0 
        ? `print-templates-${new Date().toISOString().split('T')[0]}.json`
        : `all-print-templates-${new Date().toISOString().split('T')[0]}.json`;
      
      printTemplateService.downloadTemplatesAsJson(result.templates, filename);
      setExportDialogOpen(false);
      setSelectedTemplates([]);
    } catch (err) {
      console.error('خطأ في تصدير القوالب:', err);
      setError('حدث خطأ أثناء تصدير القوالب. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  // فتح مربع حوار التصدير
  const handleOpenExportDialog = () => {
    setSelectedTemplates([]);
    setExportDialogOpen(true);
  };

  // معالجة تغيير تحديد القوالب للتصدير
  const handleExportTemplateSelect = (templateId: string) => {
    setSelectedTemplates(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  // تحديد جميع القوالب للتصدير
  const handleSelectAllTemplates = () => {
    if (selectedTemplates.length === templates.length) {
      setSelectedTemplates([]);
    } else {
      setSelectedTemplates(templates.map(t => t._id!).filter(Boolean));
    }
  };

  // فتح ملف JSON للاستيراد
  const handleOpenImportDialog = () => {
    setImportJsonError(null);
    setParsedTemplates([]);
    setImportDialogOpen(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // معالجة رفع الملف
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = printTemplateService.parseImportedTemplates(content);
        setParsedTemplates(parsed);
        setImportJsonError(null);
      } catch (err: any) {
        setImportJsonError(err.message || 'خطأ في قراءة الملف');
        setParsedTemplates([]);
      }
    };
    reader.onerror = () => {
      setImportJsonError('خطأ في قراءة الملف');
      setParsedTemplates([]);
    };
    reader.readAsText(file);
  };

  // استيراد القوالب
  const handleImportTemplates = async () => {
    if (parsedTemplates.length === 0) {
      setImportJsonError('يرجى رفع ملف صالح أولاً');
      return;
    }
    
    try {
      setImportLoading(true);
      const result = await printTemplateService.importPrintTemplates(parsedTemplates);
      
      // تحديث قائمة القوالب
      setTemplates([...templates, ...result.templates]);
      setImportDialogOpen(false);
      setParsedTemplates([]);
    } catch (err: any) {
      console.error('خطأ في استيراد القوالب:', err);
      setError(err.message || 'حدث خطأ أثناء استيراد القوالب. يرجى المحاولة مرة أخرى.');
    } finally {
      setImportLoading(false);
    }
  };

  // إغلاق مربع حوار الاستيراد
  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setParsedTemplates([]);
    setImportJsonError(null);
  };

  // فتح محرر القالب
  const handleEditTemplateDesign = (templateId: string) => {
    navigate(`/invoices/print-templates/editor/${templateId}`);
  };

  // عرض بطاقات القوالب
  const renderTemplateCards = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (templates.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          لا توجد قوالب طباعة. انقر على "إضافة قالب جديد" لإنشاء أول قالب.
        </Alert>
      );
    }

    return (
      <Grid container spacing={3}>
        {templates.map(template => (
          <Grid item xs={12} md={6} lg={4} key={template._id}>
            <Card 
              elevation={3}
              sx={{
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: theme.shadows[6]
                }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  p: 1,
                  zIndex: 1
                }}
              >
                <Tooltip title="تحرير التصميم">
                  <IconButton 
                    size="small" 
                    color="primary"
                    onClick={() => handleEditTemplateDesign(template._id!)}
                  >
                    <TemplateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="تعديل الإعدادات">
                  <IconButton size="small" onClick={() => handleOpenEditDialog(template)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="استنساخ">
                  <IconButton size="small" onClick={() => handleCloneTemplate(template._id!)}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="حذف">
                  <IconButton size="small" onClick={() => handleDeleteTemplate(template._id!)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <TextIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h2">{template.name}</Typography>
                </Box>
                {template.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {template.description}
                  </Typography>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>نوع الفاتورة:</strong> {invoiceTypes.find(t => t.value === template.invoiceType)?.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>حجم الورق:</strong> {paperSizes.find(p => p.value === template.paperSize)?.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>الاتجاه:</strong> {orientations.find(o => o.value === template.orientation)?.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>اللغة:</strong> {languages.find(l => l.value === template.displayLanguage)?.label}
                </Typography>
                <Box mt={2}>
                  {template.isDefault ? (
                    <Chip
                      label="القالب الافتراضي"
                      size="small"
                      color="success"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleSetDefaultTemplate(template._id!)}
                      sx={{ mb: 1, fontSize: '0.7rem' }}
                    >
                      تعيين كافتراضي
                    </Button>
                  )}
                  
                  <Chip
                    label={`${template.elements?.length || 0} عنصر`}
                    size="small"
                    color="primary"
                    sx={{ mb: 1, mr: 1 }}
                  />
                </Box>
                
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => handleEditTemplateDesign(template._id!)}
                >
                  تحرير التصميم
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".json"
        style={{ display: 'none' }}
      />
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h5" component="h1" gutterBottom>
            إعدادات قوالب الطباعة
          </Typography>
          <Typography variant="body1" color="text.secondary">
            قم بإنشاء وإدارة قوالب طباعة مخصصة لأنواع الفواتير المختلفة
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ImportIcon />}
            onClick={handleOpenImportDialog}
          >
            استيراد
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ExportIcon />}
            onClick={handleOpenExportDialog}
          >
            تصدير
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            إضافة قالب جديد
          </Button>
        </Box>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="template tabs">
            <Tab label="جميع القوالب" {...a11yProps(0)} />
            <Tab label="تصفية القوالب" {...a11yProps(1)} />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {renderTemplateCards()}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>نوع الفاتورة</InputLabel>
                <Select
                  value={filterType}
                  label="نوع الفاتورة"
                  onChange={(e) => handleFilterChange('invoiceType', e)}
                >
                  <MenuItem value="">جميع الأنواع</MenuItem>
                  {invoiceTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>اللغة</InputLabel>
                <Select
                  value={filterLanguage}
                  label="اللغة"
                  onChange={(e) => handleFilterChange('displayLanguage', e)}
                >
                  <MenuItem value="">جميع اللغات</MenuItem>
                  {languages.map(lang => (
                    <MenuItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {renderTemplateCards()}
        </TabPanel>
      </Paper>

      {/* مربع حوار إنشاء/تعديل قالب الطباعة */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTemplate ? 'تعديل قالب الطباعة' : 'إنشاء قالب طباعة جديد'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="اسم القالب"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>نوع الفاتورة</InputLabel>
                <Select
                  name="invoiceType"
                  value={formData.invoiceType}
                  onChange={handleInputChange}
                  label="نوع الفاتورة"
                >
                  {invoiceTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="وصف القالب"
                name="description"
                value={formData.description || ''}
                onChange={handleInputChange}
                multiline
                rows={2}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>حجم الورق</InputLabel>
                <Select
                  name="paperSize"
                  value={formData.paperSize}
                  onChange={handleInputChange}
                  label="حجم الورق"
                >
                  {paperSizes.map(size => (
                    <MenuItem key={size.value} value={size.value}>
                      {size.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>الاتجاه</InputLabel>
                <Select
                  name="orientation"
                  value={formData.orientation}
                  onChange={handleInputChange}
                  label="الاتجاه"
                >
                  {orientations.map(orientation => (
                    <MenuItem key={orientation.value} value={orientation.value}>
                      {orientation.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* أبعاد الورق المخصص - تظهر فقط عند اختيار حجم مخصص */}
            {formData.paperSize === 'custom' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="العرض (بالملم)"
                    name="customWidth"
                    type="number"
                    value={formData.customWidth || ''}
                    onChange={handleInputChange}
                    required
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="الارتفاع (بالملم)"
                    name="customHeight"
                    type="number"
                    value={formData.customHeight || ''}
                    onChange={handleInputChange}
                    required
                    margin="normal"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>اللغة</InputLabel>
                <Select
                  name="displayLanguage"
                  value={formData.displayLanguage}
                  onChange={handleInputChange}
                  label="اللغة"
                >
                  {languages.map(language => (
                    <MenuItem key={language.value} value={language.value}>
                      {language.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isDefault || false}
                      onChange={handleInputChange}
                      name="isDefault"
                      color="primary"
                    />
                  }
                  label="تعيين كقالب افتراضي"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit" startIcon={<CancelIcon />}>
            إلغاء
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
          >
            {editingTemplate ? 'حفظ التغييرات' : 'إنشاء القالب'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* مربع حوار تصدير القوالب */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ExportIcon color="primary" />
          تصدير القوالب
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            اختر القوالب التي تريد تصديرها، أو تصدير جميع القوالب.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTemplates.length === templates.length && templates.length > 0}
                  onChange={handleSelectAllTemplates}
                />
              }
              label="تحديد الكل"
            />
            <List>
              {templates.map(template => (
                <ListItem key={template._id}>
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedTemplates.includes(template._id!)}
                      onChange={() => handleExportTemplateSelect(template._id!)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={template.name}
                    secondary={`${invoiceTypes.find(t => t.value === template.invoiceType)?.label} - ${paperSizes.find(p => p.value === template.paperSize)?.label}`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
          {selectedTemplates.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              سيتم تصدير جميع القوالب ({templates.length} قالب)
            </Alert>
          )}
          {selectedTemplates.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              سيتم تصدير {selectedTemplates.length} قالب محدد
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} startIcon={<CancelIcon />}>
            إلغاء
          </Button>
          <Button
            variant="contained"
            onClick={handleExportTemplates}
            startIcon={<ExportIcon />}
            disabled={loading}
          >
            تصدير
          </Button>
        </DialogActions>
      </Dialog>

      {/* مربع حوار استيراد القوالب */}
      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ImportIcon color="primary" />
          استيراد القوالب
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            اختر ملف JSON يحتوي على قوالب طباعة للاستيراد. تأكد من أن الملف يحتوي على بيانات صالحة.
          </Typography>
          
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            startIcon={<ImportIcon />}
            fullWidth
            sx={{ mb: 2 }}
          >
            اختيار ملف
          </Button>
          
          {importJsonError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {importJsonError}
            </Alert>
          )}
          
          {parsedTemplates.length > 0 && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                تم العثور على {parsedTemplates.length} قالب في الملف
              </Alert>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>القوالب الجاهزة للاستيراد:</Typography>
                {parsedTemplates.map((template, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                    • {template.name} ({invoiceTypes.find(t => t.value === template.invoiceType)?.label})
                  </Typography>
                ))}
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} startIcon={<CancelIcon />}>
            إلغاء
          </Button>
          <Button
            variant="contained"
            onClick={handleImportTemplates}
            startIcon={<ImportIcon />}
            disabled={importLoading || parsedTemplates.length === 0}
          >
            {importLoading ? 'جاري الاستيراد...' : 'استيراد'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrintTemplateSettingsPage;